import {
  type CSSProperties,
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  watch,
} from "vue";
import type { ComputedRef, Ref } from "vue";

interface UseBubbleLabelSyncParams {
  /** Reactive option list – re-measures whenever it changes. */
  picklistOptions: Ref<any[]>;
  /** Reactive field style value – re-measures when style changes. */
  fieldStyle: Ref<string | undefined>;
  /** Whether the bubble style is currently active. */
  isBubble: ComputedRef<boolean>;
  /**
   * Minimum label width in pixels when bubble style is active.
   * Checkbox uses 20 px, Radio uses 82 px.
   * @default 20
   */
  baseMinWidth?: number;
}

/**
 * @description Keeps bubble-style option labels in sync with the widest label so
 * all bubbles share the same minimum width. Also exposes ref helpers and a computed
 * inline style that enforces the synced minimum width.
 *
 * @param params - Configuration for the bubble sync behaviour.
 * @returns Reactive label refs, measurement helpers, and the computed inline style.
 */
export const useBubbleLabelSync = ({
  picklistOptions,
  fieldStyle,
  isBubble,
  baseMinWidth = 20,
}: UseBubbleLabelSyncParams) => {
  const labelRefs = shallowRef<Array<HTMLElement | null>>([]);

  /**
   * @description Registers a label element in the ref array at the given index.
   *
   * @param el - The element to register, or null when Vue unmounts the ref.
   * @param index - The option index this label corresponds to.
   * @returns {void}
   */
  const setLabelRef = (el: HTMLElement | null, index: number): void => {
    labelRefs.value[index] = el;
    if (!el && labelRefs.value.length > picklistOptions.value.length) {
      labelRefs.value.length = picklistOptions.value.length;
    }
  };

  /**
   * @description Returns the label element for a given option index, or null.
   *
   * @param index - The option index to look up.
   * @returns {HTMLElement | null}
   */
  const getLabelRef = (index: number): HTMLElement | null => labelRefs.value[index] ?? null;

  const syncedLabelWidth = ref<number>(0);

  /**
   * @description Measures all registered label elements and sets `syncedLabelWidth`
   * to the maximum natural content width. First resets the width to zero so labels
   * can shrink before the new measurement is taken.
   *
   * @returns {void}
   */
  const measureAndSyncLabelWidths = (): void => {
    if (syncedLabelWidth.value !== 0) {
      syncedLabelWidth.value = 0;
    }
    nextTick(() => {
      let maxWidth = 0;
      for (const el of labelRefs.value) {
        if (!el) continue;
        const prevMinWidth = el.style.minWidth;
        el.style.minWidth = "0px";
        const width = Math.ceil(el.scrollWidth);
        el.style.minWidth = prevMinWidth;
        if (width > maxWidth) maxWidth = width;
      }
      syncedLabelWidth.value = maxWidth;
    });
  };

  onMounted(() => {
    measureAndSyncLabelWidths();
    window.addEventListener("resize", measureAndSyncLabelWidths);
  });

  onUnmounted(() => {
    window.removeEventListener("resize", measureAndSyncLabelWidths);
  });

  watch(
    picklistOptions,
    () => {
      labelRefs.value.length = picklistOptions.value.length;
      measureAndSyncLabelWidths();
    },
    { deep: true }
  );
  watch(fieldStyle, measureAndSyncLabelWidths);

  /**
   * @description Computed inline style that enforces the synced minimum label width
   * when bubble style is active. Returns an empty object for non-bubble styles.
   *
   * @returns {CSSProperties} `minWidth` / `maxWidth` when in bubble mode; `{}` otherwise.
   */
  const syncedLabelInlineStyle = computed((): CSSProperties => {
    if (!isBubble.value) return {} as CSSProperties;
    const targetMin = Math.max(baseMinWidth, syncedLabelWidth.value || 0);
    return { minWidth: `${targetMin}px`, maxWidth: "100%" } as CSSProperties;
  });

  return {
    labelRefs,
    setLabelRef,
    getLabelRef,
    syncedLabelWidth,
    measureAndSyncLabelWidths,
    syncedLabelInlineStyle,
  };
};
